import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coins, Plus, TrendingUp, TrendingDown, Clock, ArrowLeft, CreditCard, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencySelector } from "@/components/CurrencySelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Credits = () => {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [purchaseAmount, setPurchaseAmount] = useState(500);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [user, setUser] = useState(null);

  const {
    currency,
    supported,
    detectedCountry,
    setCurrency,
    formatFromInr,
  } = useCurrency();

  useEffect(() => {
    loadCreditsData();
    loadUserData();
    // Pre-load Razorpay script
    loadRazorpayScript();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const loadCreditsData = async () => {
    try {
      const [creditsRes, transactionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/credits`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/credits/transactions`, { withCredentials: true })
      ]);

      setCredits(creditsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error("Error loading credits:", error);
      toast.error("Failed to load credits data");
    } finally {
      setLoading(false);
    }
  };

  const handleRazorpayPayment = useCallback(async () => {
    if (purchaseAmount < 500) {
      toast.error("Minimum purchase is 500 credits");
      return;
    }

    setPurchasing(true);

    try {
      // Load Razorpay script if not already loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please refresh the page.");
        setPurchasing(false);
        return;
      }

      // Create Razorpay order
      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/razorpay/create-order`,
        {
          amount: purchaseAmount,
          order_type: "credits",
          credits_amount: purchaseAmount,
          currency,
        },
        { withCredentials: true }
      );

      const orderData = orderResponse.data;

      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_minor,
        currency: orderData.currency,
        name: "LoopAI Creator",
        description: orderData.description,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment on backend
          try {
            const verifyResponse = await axios.post(
              `${BACKEND_URL}/api/razorpay/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            if (verifyResponse.data.success) {
              toast.success(verifyResponse.data.message);
              await loadCreditsData();
              setPurchaseAmount(500);
              setShowCustom(false);
              setCustomAmount("");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPurchasing(false);
          }
        },
        prefill: {
          name: orderData.prefill?.name || user?.name || "",
          email: orderData.prefill?.email || user?.email || "",
        },
        theme: {
          color: "#7c3aed"
        },
        modal: {
          ondismiss: () => {
            setPurchasing(false);
            toast.info("Payment cancelled");
          }
        }
      };

      // Open Razorpay checkout
      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setPurchasing(false);
      });
      razorpayInstance.open();

    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPurchasing(false);
    }
  }, [purchaseAmount, user, currency]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalCostDisplay = formatFromInr(purchaseAmount); // ₹1 per credit, shown in selected currency

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto py-8">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Credits Management
              </h1>
              <p className="text-gray-600">
                Purchase and manage your AI generation credits
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              <CurrencySelector
                value={currency}
                supported={supported}
                onChange={setCurrency}
                detectedCountry={detectedCountry}
              />
              <Button
                onClick={() => navigate("/credits/analytics")}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                data-testid="view-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Usage Analytics
              </Button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {/* Current Balance */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Available Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold">{credits?.credits || 0}</p>
              <p className="text-blue-100 text-sm mt-2">Ready to use</p>
            </CardContent>
          </Card>

          {/* Total Purchased */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <TrendingUp className="w-5 h-5" />
                Total Purchased
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-gray-900">{credits?.total_purchased || 0}</p>
              <p className="text-gray-500 text-sm mt-2">All time</p>
            </CardContent>
          </Card>

          {/* Total Used */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <TrendingDown className="w-5 h-5" />
                Total Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-gray-900">{credits?.total_used || 0}</p>
              <p className="text-gray-500 text-sm mt-2">All time</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Purchase Credits */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Purchase Credits
              </CardTitle>
              <CardDescription>
                ₹1 per credit | Minimum 500 credits • Charged in {currency}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700">Credits:</span>
                  <span className="font-semibold">{purchaseAmount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Total Cost:</span>
                  <span className="text-2xl font-bold text-blue-600" data-testid="total-cost">{totalCostDisplay}</span>
                </div>
                {currency !== "INR" && (
                  <p className="text-xs text-gray-500 mt-1" data-testid="inr-equivalent">
                    ≈ ₹{purchaseAmount.toLocaleString()} INR at ₹1/credit
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">Quick Select:</p>
                <div className="grid grid-cols-3 gap-2">
                  {[500, 1000, 2000, 5000, 10000].map((amount) => (
                    <Button
                      key={amount}
                      variant="outline"
                      onClick={() => {
                        setPurchaseAmount(amount);
                        setShowCustom(false);
                      }}
                      className={purchaseAmount === amount && !showCustom ? "border-blue-600 bg-blue-50" : ""}
                    >
                      {amount}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCustom(true);
                      setCustomAmount("");
                    }}
                    className={showCustom ? "border-blue-600 bg-blue-50" : ""}
                  >
                    Custom
                  </Button>
                </div>
              </div>

              {showCustom && (
                <div className="space-y-2 p-4 bg-purple-50 rounded-lg">
                  <Label htmlFor="customAmount">Custom Amount (Min: 2000 credits)</Label>
                  <Input
                    id="customAmount"
                    type="number"
                    min="2000"
                    step="100"
                    placeholder="Enter amount (e.g., 15000)"
                    value={customAmount}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      setCustomAmount(e.target.value);
                      if (value >= 2000) {
                        setPurchaseAmount(value);
                      }
                    }}
                    data-testid="custom-amount-input"
                  />
                  <p className="text-xs text-gray-600">
                    Enter any amount of 2000 credits or more
                  </p>
                </div>
              )}

              <Button
                onClick={handleRazorpayPayment}
                disabled={purchasing || purchaseAmount < 500 || (showCustom && purchaseAmount < 2000)}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
                data-testid="purchase-button"
              >
                {purchasing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Purchase {purchaseAmount} Credits — {totalCostDisplay}
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <img src="https://razorpay.com/assets/razorpay-logo.svg" alt="Razorpay" className="h-4" />
                <span>Secured by Razorpay</span>
              </div>
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Transactions
              </CardTitle>
              <CardDescription>
                Last 10 credit transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No transactions yet</p>
                  </div>
                ) : (
                  transactions.slice(0, 10).map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`transaction-${transaction.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          transaction.type === 'purchase' 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-orange-100 text-orange-600'
                        }`}>
                          {transaction.type === 'purchase' ? (
                            <TrendingUp className="w-5 h-5" />
                          ) : (
                            <TrendingDown className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(transaction.created_at).toLocaleDateString()} {new Date(transaction.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className={`text-right ${
                        transaction.amount > 0 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        <p className="font-bold">
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                        </p>
                        <p className="text-xs text-gray-500">credits</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="mt-6 border-0 shadow-lg bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg">How Credits Work</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="text-blue-600">•</span>
                <span>Each image generation costs credits (amount varies by complexity)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600">•</span>
                <span>Credits are deducted automatically after project completion</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600">•</span>
                <span>Your remaining balance is shown when creating new projects</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600">•</span>
                <span>Purchase more credits anytime to continue creating</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600">•</span>
                <span>Credits never expire - use them whenever you want</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Credits;
